import { useFormikContext } from 'formik';
import { useContext, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import { AppContext } from 'src/app/modules/appContext/AppContext';
import GenerateFormHTMLNoValid from 'src/app/modules/component/generate-form/GenerateFormHTMLNoValid';
import { generateForm } from 'src/app/modules/utils/AppFunction';
import { hasAuthority, KEY_AUTHORITIES } from "src/app/modules/utils/Permission";
import { IBenhNhanV3ChuyenKhoa } from '../../../models/PhanHeChuyenKhoaModel';
import CustomComponent from './CustomComponent';

type Props = {
	infoBenhNhan?: IBenhNhanV3ChuyenKhoa;
	isViewModal?: boolean;
	inforResult: any;
	currentTab: number;
	selectedRow: any;
	setIsHasData?: (value: boolean) => void;
};

export default function FormPTTT({
	isViewModal,
	inforResult,
	currentTab,
	selectedRow,
	setIsHasData = () => {},
}: Readonly<Props>) {
	const { setIsLoading } = useContext(AppContext);
	const { values, setFieldValue, setValues, errors, touched } = useFormikContext<any>();
	const [listJsonGenerate, setListJsonGenerate] = useState<any>([]);
	const [listJsonGenerateExpand, setListJsonGenerateExpand] = useState<any>([]);
	const hasInitializedFromAPIRef = useRef(false);
	const hasInitializedDefaultRef = useRef(false);
	const editable = hasAuthority(KEY_AUTHORITIES.CHUYEN_KHOA.FORM_THUC_HIEN.EDIT);

	// useEffect để load data từ API khi có inforResult
	useEffect(() => {
		// Chỉ chạy khi có data từ API và chưa khởi tạo
		if (!inforResult?.surgeryInformationEntryDate || hasInitializedFromAPIRef.current) return;

		// Sử dụng functional update để tránh phụ thuộc vào values
		setValues((prevValues: any) => {
			// Merge thông minh: chỉ lấy field từ inforResult nếu chưa có trong formData
			const mergedFormData = { ...prevValues?.formData };

			// Duyệt qua các field từ inforResult và chỉ set nếu chưa có giá trị hoặc là giá trị mặc định
			Object.keys(inforResult).forEach(key => {
				const currentValue = prevValues?.formData?.[key];
				const isEmptyOrDefault = !currentValue || currentValue === '' || currentValue === null;

				// Chỉ ghi đè nếu field chưa có giá trị (tránh đè data từ tab khác)
				if (isEmptyOrDefault) {
					mergedFormData[key] = inforResult[key];
				}
			});

			// Xử lý các field đặc biệt (object)
			mergedFormData.surgeryComorbidity = {
				code: inforResult?.surgeryComorbidity,
				name: inforResult?.surgeryComorbidityText,
			};
			mergedFormData.surgeryInitialDiagnosis = {
				code: inforResult?.surgeryInitialDiagnosis,
				name: inforResult?.surgeryInitialDiagnosisText,
			};
			mergedFormData.surgeryPreoperativeDiagnosis = {
				code: inforResult?.surgeryPreoperativeDiagnosis,
				name: inforResult?.surgeryPreoperativeDiagnosisText,
			};
			mergedFormData.surgeryPostoperativeDiagnosis = {
				code: inforResult?.surgeryPostoperativeDiagnosis,
				name: inforResult?.surgeryPostoperativeDiagnosisText,
			};

			return {
				...prevValues,
				formData: mergedFormData,
				isViewModal: !editable || isViewModal
			};
		});

		setIsHasData(true);
		hasInitializedFromAPIRef.current = true;
	}, [inforResult, editable, isViewModal, setValues, setIsHasData]);

	// useEffect để set default values từ selectedRow
	useEffect(() => {
		if (hasInitializedFromAPIRef.current) return; // Nếu đã có data từ API thì không cần set default
		if (hasInitializedDefaultRef.current) return; // Đã set default rồi
		if (!selectedRow?.name) return;

		// Sử dụng functional update để tránh phụ thuộc vào values
		setValues((prevValues: any) => {
			// Kiểm tra nếu đã có giá trị thì không set
			if (prevValues?.formData?.surgerySurgicalProcedure && prevValues?.formData?.surgerySurgicalMethod) {
				return prevValues;
			}

			return {
				...prevValues,
				formData: {
					...prevValues?.formData,
					surgerySurgicalProcedure: prevValues?.formData?.surgerySurgicalProcedure || selectedRow?.name,
					surgerySurgicalMethod: prevValues?.formData?.surgerySurgicalMethod || selectedRow?.name,
				},
				isViewModal: !editable || isViewModal
			};
		});

		hasInitializedDefaultRef.current = true;
	}, [selectedRow, editable, isViewModal, setValues]);

	const getListform = async () => {
		let data = await generateForm("thongTinPTTT", setIsLoading);
		setListJsonGenerate(data);
	}; 


	const getListformExpand = async (executionFormExpand: string) => {
		let data = await generateForm(executionFormExpand, setIsLoading);
		setListJsonGenerateExpand(data);
	};

	useEffect(() => {
		getListform();
	}, []);

	useEffect(() => {
		selectedRow?.attributes?.executionFormExpand && getListformExpand(selectedRow?.attributes?.executionFormExpand);
	}, [selectedRow?.attributes?.executionFormExpand]);

	// useEffect để tự động update surgeryEndTime khi surgeryStartTime thay đổi
	useEffect(() => {
		const surgeryStartTime = values?.formData?.surgeryStartTime;

		if (!surgeryStartTime) return;

		// Ép kiểu surgeryStartTime sang moment và kiểm tra valid
		const startTime = moment(surgeryStartTime);
		if (!startTime.isValid()) return;

		// Hàm parse number an toàn, trả về null nếu không hợp lệ
		const parseNumber = (value: any): number | null => {
			if (value === null || value === undefined || value === '') return null;
			const num = Number(value);
			return isNaN(num) ? null : num;
		};

		// Lấy duration, dùng ?? để xử lý trường hợp giá trị = 0
		const executionDuration = parseNumber(values?.formData?.executionDuration);
		const minExecutionMinutes = parseNumber(values?.formData?.minExecutionMinutes);
		const durationInMinutes = executionDuration ?? minExecutionMinutes ?? 5;

		// Clone trước khi add để tránh mutate object gốc
		const endTime = startTime.clone().add(durationInMinutes, 'minutes');

		// Update surgeryEndTime - dùng format() để giữ format nhất quán với surgeryStartTime (local time)
		setFieldValue('formData.surgeryEndTime', endTime.format('YYYY-MM-DDTHH:mm:ss'));
	}, [values?.formData?.surgeryStartTime, values?.formData?.executionDuration, values?.formData?.minExecutionMinutes, setFieldValue]);

	return (
		<>
			<Modal.Body className="dialog-body dialog-body-PTTT padding-3-20">
				<GenerateFormHTMLNoValid
					listFieldAuto={listJsonGenerate}
					modelID="formData"
					values={values}
					setFieldValue={setFieldValue}
					customComponent={CustomComponent}
					errors={errors}
					touched={touched}
					isView={!editable || isViewModal}
				/>
				{listJsonGenerateExpand && (
					<div className='mt-2'>
						<GenerateFormHTMLNoValid
							listFieldAuto={listJsonGenerateExpand}
							modelID="formData"
							values={values}
							setFieldValue={setFieldValue}
							customComponent={CustomComponent}
							errors={errors}
							touched={touched}
							isView={!editable || isViewModal}
						/>
					</div>
				)}
			</Modal.Body>
		</>
	);
}
